'use strict';

var stylePromises = {};

/**
 * Finds an existing stylesheet by comparing resolved href values.
 *
 * @param {string} href - Stylesheet URL to resolve.
 * @returns {HTMLLinkElement|null} Existing stylesheet element when found.
 */
function findExistingStylesheet(href) {
    var resolvedHref = new URL(href, window.location.href).href;
    var stylesheets = document.querySelectorAll('link[rel="stylesheet"]');

    for (var index = 0; index < stylesheets.length; index++) {
        if (stylesheets[index].href === resolvedHref) {
            return stylesheets[index];
        }
    }

    return null;
}

/**
 * Loads an external stylesheet once and reuses the same promise for repeat requests.
 *
 * @param {string} href - Stylesheet URL to load.
 * @returns {Promise<HTMLLinkElement>} Resolves when the stylesheet is loaded.
 */
module.exports = function loadExternalStyle(href) {
    if (!href) {
        return Promise.reject(new Error('Missing stylesheet source.'));
    }

    if (stylePromises[href]) {
        return stylePromises[href];
    }

    stylePromises[href] = new Promise(function (resolve, reject) {
        var existingStyle = findExistingStylesheet(href);

        if (existingStyle) {
            if (existingStyle.getAttribute('data-loaded') === 'true' || existingStyle.sheet) {
                existingStyle.setAttribute('data-loaded', 'true');
                resolve(existingStyle);
                return;
            }

            existingStyle.addEventListener('load', function handleExistingLoad() {
                existingStyle.setAttribute('data-loaded', 'true');
                resolve(existingStyle);
            }, { once: true });
            existingStyle.addEventListener('error', function handleExistingError() {
                delete stylePromises[href];
                reject(new Error('Failed to load stylesheet: ' + href));
            }, { once: true });
            return;
        }

        var style = document.createElement('link');
        style.rel = 'stylesheet';
        style.href = href;
        style.setAttribute('data-loader-href', href);

        style.onload = function () {
            style.setAttribute('data-loaded', 'true');
            resolve(style);
        };

        style.onerror = function () {
            delete stylePromises[href];
            reject(new Error('Failed to load stylesheet: ' + href));
        };

        document.head.appendChild(style);
    });

    return stylePromises[href];
};
